import { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FaFile, FaFileArchive, FaFileExcel, FaFileImage, FaFilePdf, FaFileWord } from 'react-icons/fa';
import { MdOutlineClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { clearUploadedFiles, uploadFiles } from '../store/Slices/FileSlice';
import '../styles/FileUpload.css';
import { uploadIcon } from '../utils/svgs';
import { errorNotify, successNotify } from '../utils/toast';

const FileUpload = ({ destination = 'service-entry', files = [], setFiles, maxFileSize = 10, className = '', disabled = false }) => {
    const dispatch = useDispatch();

    const { uploadLoading, uploadedFiles } = useSelector((state) => state.files);

    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);

    const handleDrag = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = async (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const formData = new FormData();
            formData.append('media', e.dataTransfer.files[0]);
            formData.append('destination', destination);

            await dispatch(uploadFiles(formData)).unwrap();
            successNotify('File Uploaded successfully');
        }
    };

    const handleChange = async (e) => {
        if (disabled) return;
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            const formData = new FormData();
            formData.append('media', e.target.files[0]);
            formData.append('destination', destination);

            try {
                const result = await dispatch(uploadFiles(formData)).unwrap();
                if (!result || result.error) {
                    errorNotify(result?.error || 'File upload failed');
                } else {
                    successNotify('File Uploaded successfully');
                }
            } catch (err) {
                errorNotify(err?.message || 'File upload failed');
            }
        } else {
            errorNotify('No file selected for upload');
        }
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();

        switch (extension) {
            case 'pdf':
                return <FaFilePdf style={{ color: '#d32f2f' }} />;
            case 'doc':
            case 'docx':
                return <FaFileWord style={{ color: '#1976d2' }} />;
            case 'xls':
            case 'xlsx':
                return <FaFileExcel style={{ color: '#388e3c' }} />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
            case 'webp':
                return <FaFileImage style={{ color: '#ff9800' }} />;
            case 'zip':
            case 'rar':
            case '7z':
                return <FaFileArchive style={{ color: '#6d4c41' }} />;
            default:
                return <FaFile style={{ color: '#666' }} />;
        }
    };

    const isImageFile = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension);
    };

    const handleFiles = async (filesArr) => {
        const newFiles = [];

        for (let i = 0; i < filesArr.length; i++) {
            const filePath = filesArr[i];
            const isImage = isImageFile(filePath);
            newFiles.push({
                originalName: filePath,
                fileName: filePath,
                previewUrl: isImage ? `${process.env.REACT_APP_API_FILES_URL}${filePath}` : null,
                backend: true
            });
        }

        setFiles(prev => {
            const existingFileNames = prev.map(f => f.fileName);
            const filteredNewFiles = newFiles.filter(f => !existingFileNames.includes(f.fileName));
            return [...prev, ...filteredNewFiles];
        });

        if (inputRef.current) {
            inputRef.current.value = '';
        }

        dispatch(clearUploadedFiles())
    };

    useEffect(() => {
        if (uploadedFiles && uploadedFiles.length > 0) {
            handleFiles(uploadedFiles);
        }
    }, [uploadedFiles]);

    const onButtonClick = () => {
        if (disabled) return;
        inputRef.current?.click();
    };

    const removeFile = (indexToRemove) => {
        const fileToRemove = files[indexToRemove];

        if (fileToRemove.previewUrl) {
            URL.revokeObjectURL(fileToRemove.previewUrl);
        }

        const updatedFiles = files.filter((_, index) => index !== indexToRemove);
        setFiles(updatedFiles);
    };

    return (
        <div className={`file_upload_container ${className} ${disabled ? 'file_upload_disabled' : ''}`}> 
            <div
                className={`file_upload_area ${dragActive ? 'drag_active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={onButtonClick}
                style={disabled ? { pointerEvents: 'none', opacity: 0.6, cursor: 'not-allowed' } : null}
            >
                <input
                    ref={inputRef}
                    type="file"
                    onChange={handleChange}
                    style={{ display: 'none' }}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    disabled={disabled}
                />
                <div className='upload_content'>
                    <span className='upload_icon'>{uploadIcon}</span>
                    <div className='upload_text'>
                        <span>Drag file or Browse to select.</span>
                        <span>Max file size: {maxFileSize}MB per file</span>
                    </div>
                </div>
            </div>

            {uploadLoading && (
                <div className='upload_loading'>
                    <span>Processing files...</span>
                </div>
            )}

            {files.length > 0 && (
                <div className='uploaded_files_list'>
                    <Row>
                        {files.map((file, index) => (
                            <Col md={6} key={index}>
                                <div className='uploaded_file_item'>
                                    <div className='file_preview'>
                                        <a
                                            href={file.previewUrl || `${process.env.REACT_APP_API_FILES_URL}${file.fileName}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ display: 'inline-block' }}
                                        >
                                            {isImageFile(file.originalName) && file.previewUrl ? (
                                                <img
                                                    src={file.previewUrl}
                                                    alt={file.originalName}
                                                    className='file_image_preview'
                                                />
                                            ) : (
                                                <div className='file_icon_preview'>
                                                    {getFileIcon(file.originalName)}
                                                </div>
                                            )}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFile(index);
                                            }}
                                            className='remove_file_btn'
                                        >
                                            <MdOutlineClose />
                                        </button>
                                    </div>
                                    <div className='file_info'>
                                        <span className='file_name' title={file.originalName}>
                                            {file.originalName}
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                    <div className='files_grid'>

                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;
